/**
 * Safe localStorage operations that handle errors gracefully.
 *
 * localStorage can throw errors in:
 * - Private/incognito browsing mode (some browsers)
 * - When quota is exceeded
 * - When localStorage is disabled by user/policy
 *
 * These utilities provide error-safe wrappers that log errors
 * and return sensible fallback values.
 */

/**
 * Safely get an item from localStorage.
 * Returns null if the item doesn't exist or if an error occurs.
 *
 * @param key - The localStorage key
 * @returns The stored value or null
 */
export function safeGetItem(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch (error) {
    console.warn(`[safeLocalStorage] Error reading from localStorage (key: ${key}):`, error);
    return null;
  }
}

/**
 * Safely set an item in localStorage.
 * Returns true if successful, false if an error occurs.
 *
 * @param key - The localStorage key
 * @param value - The value to store
 * @returns true if successful, false otherwise
 */
export function safeSetItem(key: string, value: string): boolean {
  try {
    localStorage.setItem(key, value);
    return true;
  } catch (error) {
    console.warn(`[safeLocalStorage] Error writing to localStorage (key: ${key}):`, error);
    return false;
  }
}

/**
 * Safely remove an item from localStorage.
 * Returns true if successful, false if an error occurs.
 *
 * @param key - The localStorage key
 * @returns true if successful, false otherwise
 */
export function safeRemoveItem(key: string): boolean {
  try {
    localStorage.removeItem(key);
    return true;
  } catch (error) {
    console.warn(`[safeLocalStorage] Error removing from localStorage (key: ${key}):`, error);
    return false;
  }
}
